/**
 * Modellserier, del 2 — fler serier i samma familj som src/data/modell-serier.ts.
 *
 * Innehållet bygger på våra egna inbyten, fuktprotokoll och verkstadsjobb i
 * Kalix. Prisspann är observationer på svensk begagnatmarknad, inte utfästelser.
 * Filen är separerad enbart för att hålla varje fil läsbar — datan konsumeras
 * via MODELL_SERIER i modell-serier.ts.
 */

import type { ModellSerie } from "./modell-serier";

export const MODELL_SERIER_2: ModellSerie[] = [
  {
    brandSlug: "kabe",
    brandName: "Kabe",
    category: "husvagn",
    slug: "imperial",
    name: "Kabe Imperial",
    h1: "Kabe Imperial — toppserien när inget får saknas",
    metaTitle: "Kabe Imperial begagnad: pris per årsmodell & vad vi kollar",
    metaDesc:
      "Kabe Imperial begagnat: prisspann 2015–2023, planlösningar, vinterprestanda vid -30 grader, återkommande fel i verkstaden och vår kontroll före köp i Kalix.",
    keyword: "kabe imperial",
    intro:
      "Imperial är Kabes flaggskepp och den vagn vi ser hos kunder som bor i vagnen flera månader per år. Skillnaden mot Royal ligger i inredningsmaterial, extra isolering i taket och mer el redan från fabrik.",
    layouts: [
      "Imperial 780 med långbädd i sidan, separat duschkabin och hög ståhöjd i våtrummet",
      "Imperial 880 med enkelsängar bak, mittdusch och rundsittgrupp för sex personer",
      "Imperial 1000 — tvåaxlad med skiljedörr, planlösningen vi säljer mest till åretruntboende",
    ],
    perfectFor: [
      "Långtidscamping där vagnen används 100 nätter eller mer per år",
      "Den som vill ha golvvärme, 230 V-uttag och stor kylskåpsvolym monterat från fabrik",
      "Ekipage med dragbil över 2 500 kg tjänstevikt och BE-behörighet",
    ],
    strengths: [
      "60 mm isolering i tak och vägg mot 45 mm i mellanserierna",
      "Golvvärme via vattenslinga plus AGS-ventilation i dubbelgolvet",
      "Fabriksmonterat förbrukningsbatteri på 130 Ah och laddare kring 25 A",
      "Låg värdeminskning — 5–7 procent per år är vad vi ser på våra inbyten",
    ],
    weaknesses: [
      "Tjänstevikt kring 2 100–2 600 kg gör att många personbilar faller bort som dragbil",
      "Tvåaxlade exemplar kräver större uppställningsyta och är tunga att rangera för hand",
      "Reparation av panelinredningen är dyrare än på volymvagnar",
    ],
    winter:
      "Vi har mätt kupétemperatur på Imperial-vagnar vid -31 grader i Kalix: med gasolvärme på halvfart låg golvytan runt 19 grader och fönsterkanterna torra. Förutsättningen är att AGS-ventilerna är öppna och att snön skottas av taket vid mer än cirka 30 cm.",
    priceNew: "Från cirka 780 000 kr för de kortare modellerna",
    priceUsed:
      "2015–2017: 330 000–470 000 kr · 2018–2020: 470 000–640 000 kr · 2021–2023: 640 000–890 000 kr",
    vanligaFel: [
      "Kondens bakom överskåp när tilluftsgallren täckts av inredning eller packning",
      "Slitna dörrtätningslister på exemplar över 7 år, vilket ger drag vid golvvinkeln",
      "Läckande blandare i våtrummet efter frysskada vid felaktig vinteravställning",
      "Trög justering på tvåaxlade bromsar när dammskydden släppt vatten",
    ],
    checklist: [
      "Fuktmätning i 12 punkter — vi stoppar affären över 20 procent",
      "Tryckprov på färskvattensystemet för att hitta frysspruckna kopplingar",
      "Kontroll av AGS-kanaler och att alla golvventiler går att öppna helt",
      "Genomgång av gasolsystem inklusive datummärkning på slangar och regulator",
      "Läsning av laddarens felkod och kapacitetstest av förbrukningsbatteriet",
    ],
    djup: [
      "Det som skiljer Imperial från övriga Kabe-serier är inte i första hand komforten utan hur konstruktionen beter sig över tid. Extra tjock isolering betyder att innerpanelen håller högre yttemperatur, och eftersom kondens uppstår när fuktig luft möter kalla ytor får en Imperial helt enkelt färre platser där vatten kan bildas. I våra fuktprotokoll ligger 10 år gamla Imperial-vagnar ofta på 9–13 procent i bakre hörnen, medan volymvagnar i samma ålder ofta ligger på 14–18 procent.",
      "Vikten är den stora frågan vid köp. En tvåaxlad Imperial 1000 har totalvikt runt 2 800–3 000 kg och kräver både BE-behörighet och en dragbil med hög dragvikt. Vi räknar alltid igenom ekipaget med vatten i tanken, två gasolflaskor och normal packning innan kontrakt — annars blir vagnen olagligt lastad redan vid första resan.",
      "Elsystemet är värt att inspektera noga på exemplar från 2018 och framåt, eftersom fler kunder eftermonterat solpaneler och litiumbatterier. Vi kontrollerar att laddarens laddprofil verkligen är omställd till litium; annars kan batteriet ligga på 14,4 V långt efter fulladdning, vilket kortar livslängden med flera år.",
      "Andrahandsvärdet gör Imperial billigare att äga än många tror. En vagn köpt för 800 000 kr ligger efter fem år ofta kring 560 000–620 000 kr. Räknat på 60 campingnätter per år hamnar värdeminskningen kring 700–800 kr per natt, vilket är i nivå med en enklare vagn som tappar procentuellt mycket mer.",
    ],
    faq: [
      {
        q: "Vad skiljer Kabe Imperial från Royal?",
        a: "Imperial har mer isolering, exklusivare inredningsmaterial och mer utrustning monterad från fabrik. Royal är byggd på samma vinterkoncept men med enklare ytskikt och färre tillval som standard.",
      },
      {
        q: "Vilken dragbil krävs?",
        a: "För de enaxlade modellerna räcker ofta en bil med 2 500 kg dragvikt, men tvåaxlade Imperial behöver i praktiken 3 000 kg dragvikt och BE-körkort.",
      },
      {
        q: "Är Imperial värd merpriset begagnat?",
        a: "Om du campar mycket på vintern, ja. Campar du främst i juli finns billigare vagnar som ger samma upplevelse för lägre pengar.",
      },
    ],
  },
  {
    brandSlug: "kabe",
    brandName: "Kabe",
    category: "husvagn",
    slug: "briljant",
    name: "Kabe Briljant",
    h1: "Kabe Briljant — instegsvagnen med Kabe-DNA",
    metaTitle: "Kabe Briljant begagnad: pris, vikter och vanliga fel",
    metaDesc:
      "Kabe Briljant begagnad husvagn: vad den kostar 2013–2021, hur den klarar kyla, vilka fel vi ser i verkstaden och vad du bör mäta innan du köper.",
    keyword: "kabe briljant",
    intro:
      "Briljant är vägen in i Kabe-världen för den som inte behöver dubbelt golv överallt. Vi rekommenderar den ofta till familjer som campar från april till oktober men vill ha kvar Kabes byggkvalitet.",
    layouts: [
      "Briljant 560 med våningssäng bak — vanligast bland barnfamiljer i Norrbotten",
      "Briljant 600 med enkelsängar och våtrum längst bak",
      "Briljant 470 som kort och lätt vagn för mindre dragbilar",
    ],
    perfectFor: [
      "Familjer med två till tre barn som campar under sommarhalvåret",
      "Förstagångsköpare som vill ha kvalitet men inte betala flaggskeppspris",
      "Dragbilar med 1 800–2 200 kg dragvikt",
    ],
    strengths: [
      "Låg tjänstevikt från cirka 1 250 kg gör vagnen körbar bakom vanliga familjebilar",
      "Kabes stomkonstruktion med samma tätningsprinciper som dyrare serier",
      "Enkel att sälja vidare — efterfrågan på begagnade Briljant är stabil",
      "Lägre driftkostnad: mindre yta att värma ger lägre gasolförbrukning",
    ],
    weaknesses: [
      "Saknar helt dubbelt golv på flera årsmodeller — sämre skydd för vattenledningar",
      "Enklare köksinredning och mindre kylvolym än Royal",
      "Vid längre vinterbruk krävs eftermontering av tankvärmare",
    ],
    winter:
      "Briljant fungerar för helgvinterresor med elanslutning, men vi avråder från uppställning i sträng kyla utan tillsyn. Vid -20 grader har vi sett att färskvattenslangen under bänken fryser inom ett par timmar om värmen slås av.",
    priceNew: "Nyproduktion begränsad — jämförbara nya Kabe-modeller från cirka 480 000 kr",
    priceUsed:
      "2013–2015: 130 000–195 000 kr · 2016–2018: 190 000–270 000 kr · 2019–2021: 265 000–360 000 kr",
    vanligaFel: [
      "Fukt runt bakre stötfångarinfästning där stenskott slitit på tätmassan",
      "Sprickor i takluckans plast efter 8–10 säsonger av UV och kyla",
      "Glapp i stödbenens gängor när de körts med batteriborrmaskin på hög fart",
      "Kylskåp som inte startar på gasol på grund av sotad brännare",
    ],
    checklist: [
      "Fuktmätning i 12 punkter med extra fokus på bakvägg och fönsterhörn",
      "Kontroll av hjullager och däckens DOT-datum — över 6 år byter vi",
      "Funktionsprov av kylskåp i alla tre driftlägen under minst 4 timmar",
      "Test av stödbenens gänga och smörjning inför avlämning",
      "Kontroll av kabelstammen till bromsljus, som ofta skavt vid dragkroken",
    ],
    djup: [
      "Briljant blir intressant när man räknar totalkostnad. En vagn för 220 000 kr med 8–10 procent årlig värdeminskning kostar runt 20 000 kr per år i värdefall, mot uppemot 45 000 kr för en ny toppvagn. För en familj som campar 25 nätter per sommar är skillnaden stor nog att finansiera flera resor.",
      "Konstruktionsmässigt delar Briljant limningsprinciper och tätningsmetoder med de dyrare serierna, men utan varmluftsförsedd dubbelgolvskonstruktion. Det betyder att vagnen inte torkar ut sig själv på samma sätt. Vår erfarenhet är att tätningsunderhåll vart tredje år, i stället för vart femte, håller fuktvärdena nere under 12 procent även på 10 år gamla exemplar.",
      "Vi ser många Briljant med eftermonterade tillbehör: markis, cykelhållare på bakväggen och extra solpanel. Bakmonterad cykelhållare är den vanligaste orsaken till fuktskada vi hittar, eftersom infästningen belastar bakväggen dynamiskt på dålig väg. Vi mäter alltid runt de infästningarna särskilt noga och avråder från fler än två cyklar bak.",
      "Vid uppgradering senare i livet är Briljant en bra plattform: kunder byter typiskt upp till Royal efter tre till fem år och får då ofta 70–80 procent av sitt ursprungliga inköpspris tillbaka i inbyte hos oss, förutsatt rent fuktprotokoll och kompletta servicehandlingar.",
    ],
    faq: [
      {
        q: "Klarar Kabe Briljant vintercamping?",
        a: "Med el och tillsyn fungerar helgresor bra ner mot -15 grader. För längre uppställning i kyla rekommenderar vi en vagn med dubbelt golv.",
      },
      {
        q: "Vilken årsmodell är bäst prisvärd?",
        a: "2016–2018 ger modern el och LED-belysning till betydligt lägre pris än de senaste årsmodellerna.",
      },
      {
        q: "Hur mycket lastvikt finns kvar?",
        a: "Ofta 150–250 kg beroende på utrustning. Räkna med att markis, cyklar och vatten äter upp det snabbt.",
      },
    ],
  },
  {
    brandSlug: "polar",
    brandName: "Polar",
    category: "husvagn",
    slug: "original",
    name: "Polar Original",
    h1: "Polar Original — svenskbyggd vagn för nordiskt klimat",
    metaTitle: "Polar Original begagnad: prisspann, fel och vinterprestanda",
    metaDesc:
      "Polar Original som begagnad husvagn: pris per årsmodell, planlösningar, hur den klarar norrländsk vinter och vilka fel vi återkommande åtgärdar.",
    keyword: "polar original husvagn",
    intro:
      "Original är Polars breda serie och en vagn vi handlar med ofta i Norrbotten. Den ger nordisk isolering och praktisk inredning till lägre pris än premiumkonkurrenterna.",
    layouts: [
      "Original 590 med enkelsängar bak och kök i mitten",
      "Original 620 familjevagn med våningssäng och stor rundsittgrupp",
      "Original 520 kompaktmodell för dragbilar med begränsad dragvikt",
    ],
    perfectFor: [
      "Den som vill ha nordisk vinterberedskap utan att betala för flaggskepp",
      "Familjer som campar mycket på fasta säsongsplatser",
      "Köpare med budget mellan 150 000 och 350 000 kr",
    ],
    strengths: [
      "Isolering anpassad för svenskt klimat med varmluftskanaler längs ytterväggarna",
      "Robust och enkel inredning som går att laga med standarddelar",
      "Bra pris i förhållande till utrustningsnivå på begagnatmarknaden",
      "Lättare än jämförbara premiumvagnar — ofta 100–200 kg lägre tjänstevikt",
    ],
    weaknesses: [
      "Ytskikten slits synligt snabbare än på Kabe i samma ålder",
      "Vissa årsmodeller har svag ventilation i garderoberna",
      "Reservdelsförsörjningen på äldre exemplar kan kräva anpassning",
    ],
    winter:
      "Original håller värmen bra ner till -20 grader med gasol och el i kombination. Vid hårdare kyla ser vi kalla golvvinklar i bakvagnen, och där rekommenderar vi en golvmatta med isolerande underlägg samt att inte blockera varmluftsutsläppen bakom soffan.",
    priceNew: "Nya jämförbara Polar-modeller från cirka 430 000 kr",
    priceUsed:
      "2012–2015: 105 000–175 000 kr · 2016–2018: 170 000–245 000 kr · 2019–2022: 240 000–390 000 kr",
    vanligaFel: [
      "Fuktvärden vid fönsterlisternas nedre hörn på exemplar över 9 år",
      "Skavd kabelgenomföring vid dragstången som ger intermittent bromsljusfel",
      "Läckande vattenpump med sprucken slangnippel efter frostnatt",
      "Trasiga plastbeslag i garderobsupphängningen på tidiga årsmodeller",
    ],
    checklist: [
      "12-punkters fuktmätning med referensmätning på torr innervägg",
      "Genomgång av varmluftskanaler och rensning av ludd i utsläppen",
      "Kontroll av hjulbultarnas åtdragning till korrekt moment före leverans",
      "Provtryckning av vattensystemet vid 2 bar under 15 minuter",
      "Kontroll av dragkulskopplingens slitagemarkering och stötdämparfunktion",
    ],
    djup: [
      "Polar bygger för nordiska förhållanden, och det märks mest i hur varmluften fördelas. I stället för att enbart värma golvet leds luft upp längs ytterväggarna. Praktiskt innebär det att kondensen hamnar längre från de kritiska limfogarna, vilket vi ser i fuktprotokollen: fönsterhörnen är den svaga punkten, inte väggmitten.",
      "Prismässigt är Original en av de mest prisvärda vagnarna på begagnatmarknaden just nu. En 2017-vagn för runt 210 000 kr har typiskt 6–8 procents årlig värdeminskning kvar, vilket ger cirka 15 000 kr per år i värdefall. Det är ungefär hälften av vad en fem år yngre premiumvagn tappar i kronor räknat.",
      "Vid inbyte tittar vi hårt på tre saker: fuktprotokoll, chassits underrede och bromsarnas justering. Underredet är avgörande i Norrbotten eftersom vägsaltet på E4:an äter på oskyddade infästningar. Vi rostskyddsbehandlar underredet på de exemplar vi säljer vidare när ytrost förekommer, vilket förlänger livslängden märkbart.",
      "En detalj många missar: garderobsventilationen. På flera årsmodeller är ventilationshålen bakom garderoben små, och när man packar full garderob stannar luften. Vi borrar upp kompletterande ventilation vid behov — ett ingrepp som kostar lite men som förhindrar mögellukt som annars sänker vagnens värde med tiotusentals kronor.",
    ],
    faq: [
      {
        q: "Är Polar Original en vintervagn?",
        a: "Den klarar vinterbruk med el och tillsyn, men för uppställning i djup kyla under lång tid är en vagn med dubbelt golv säkrare.",
      },
      {
        q: "Hur mycket kostar en Polar Original i drift?",
        a: "Räkna med 4 000–7 000 kr per år i service, försäkring exkluderat, plus gasol vid vinterbruk.",
      },
      {
        q: "Vad ska jag mäta först på en visning?",
        a: "Fukt i bakre hörn och under fönstren. Är de värdena rena är resten oftast hanterbart.",
      },
    ],
  },
  {
    brandSlug: "cabby",
    brandName: "Cabby",
    category: "husvagn",
    slug: "caienna",
    name: "Cabby Caienna",
    h1: "Cabby Caienna — svensk komfort med lågt insteg",
    metaTitle: "Cabby Caienna begagnad: pris, planlösning och vanliga fel",
    metaDesc:
      "Cabby Caienna begagnad husvagn: prisspann per årsmodell, vad som är starkt och svagt i konstruktionen samt vår kontroll av fukt, gasol och chassi.",
    keyword: "cabby caienna",
    intro:
      "Caienna är Cabbys mest sålda serie och en vagn vi ofta ser i inbyte från kunder som campat på samma plats i många år. Låg insteghöjd och genomtänkt förvaring är seriens signum.",
    layouts: [
      "Caienna 620 med enkelsängar bak och stort våtrum i mitten",
      "Caienna 740 tvåaxlad med skiljedörr och separat sovrum",
      "Caienna 560 med fransk bädd och kompakt kök",
    ],
    perfectFor: [
      "Par som söker låg insteghöjd och lätt tillgänglig inredning",
      "Säsongscampare på fast plats där vagnen står uppställd hela sommaren",
      "Den som vill ha svensktillverkad vagn med god förvaring",
    ],
    strengths: [
      "Låg golvhöjd som gör vagnen lättare att kliva in i för äldre campare",
      "Genomtänkt förvaring med djupa köksskåp och stort sänggarage",
      "Behaglig inomhusakustik tack vare mjukare inredningspaneler",
      "Prisvärt begagnat eftersom märket är mindre känt utanför Sverige",
    ],
    weaknesses: [
      "Reservdelar till äldre modeller kan ta längre tid att få hem",
      "Isoleringen är svagare än nordiska premiumvagnar vid sträng kyla",
      "Andrahandsvärdet är känsligare för skick och dokumentation",
    ],
    winter:
      "Caienna är främst en sommar- och höstvagn. Vid -10 grader håller den värmen bra på el, men under -20 blir bakvagnens golv märkbart kallt och vi rekommenderar tilläggsisolering under sängbotten samt frostvakt om vagnen står obemannad.",
    priceNew: "Ny produktion har upphört i sin tidigare form — jämförbara nya vagnar från 420 000 kr",
    priceUsed:
      "2010–2013: 75 000–135 000 kr · 2014–2016: 130 000–200 000 kr · 2017–2019: 195 000–295 000 kr",
    vanligaFel: [
      "Fuktvandring från takluckan ner i takpanelen på exemplar över 10 år",
      "Sprucken plastdetalj i myggnätets rullmekanism",
      "Bromsvajrar som kärvar efter vintercamping utan efterföljande smörjning",
      "Läckage vid duschblandarens genomföring i våtrumsväggen",
    ],
    checklist: [
      "Fuktmätning i 12 punkter samt kontroll runt takluckor och ventiler",
      "Okulär besiktning av takdukens skarvar med stege och fuktindikator",
      "Kontroll av dragkulans slitage och att stötdämparen i dragröret fungerar",
      "Funktionsprov av gasolvärmare i minst 30 minuter med temperaturlogg",
      "Kontroll av chassinummer mot registreringsbevis och tidigare skadehistorik",
    ],
    djup: [
      "Caienna har ett gott rykte bland dem som prioriterar tillgänglighet. Golvhöjden är typiskt 5–10 cm lägre än på flera konkurrenter, vilket låter litet men gör stor skillnad för den som har svårt med knän eller höfter. Vi säljer flera Caienna per år just till kunder som bytt från en vagn med brant insteg.",
      "Eftersom märket är mindre efterfrågat på exportmarknaden är prisbilden gynnsam för köparen. En välskött 2016-vagn kan ligga 30 000–60 000 kr under motsvarande vagn från ett större märke, trots likvärdigt skick. Baksidan är att samma effekt drabbar dig när du säljer vidare, så räkna med att äga vagnen längre för att räkningen ska gå ihop.",
      "Vi lägger extra tid på takgenomföringar när vi tar in en Caienna. Många exemplar har eftermonterade solpaneler, och en olämpligt monterad panelfot är en vanlig läckagepunkt. Rätt utförd montering använder limmade fötter utan genomgående skruv, eller genomföring med butylband plus toppförsegling — annars kommer fukten in inom några år.",
      "För säsongscampare är förvaringen det starkaste argumentet. Sänggaraget rymmer typiskt två fällbara stolar, ett bord och förtältsstänger utan att inkräkta på boendeytan. Vi rekommenderar dock att inte lasta mer än 60–70 kg där bak, eftersom kultrycket snabbt hamnar utanför tillåtet intervall.",
    ],
    faq: [
      {
        q: "Är Cabby Caienna bra begagnat?",
        a: "Ja, om fuktprotokollet är rent. Prisbilden är förmånlig och konstruktionen är gedigen för sommar- och höstbruk.",
      },
      {
        q: "Får jag tag i reservdelar?",
        a: "Till det mesta ja, via standardleverantörer för kyl, värmare och chassi. Märkesspecifika inredningsdetaljer kan ta någon vecka.",
      },
      {
        q: "Passar den för vinterboende?",
        a: "Inte som förstahandsval. För boende i kyla rekommenderar vi vagn med dubbelt golv och varmluftsförsedda kanaler.",
      },
    ],
  },
  {
    brandSlug: "knaus",
    brandName: "Knaus",
    category: "husvagn",
    slug: "sport",
    name: "Knaus Sport",
    h1: "Knaus Sport — mycket vagn för pengarna",
    metaTitle: "Knaus Sport begagnad husvagn: pris, vikter och kontrollpunkter",
    metaDesc:
      "Knaus Sport begagnad: prisspann 2014–2023, planlösningar, hur den klarar svensk vinter, vanliga fel i verkstaden och vår 12-punkters fuktkontroll.",
    keyword: "knaus sport husvagn",
    intro:
      "Sport är Knaus volymserie och den vagn vi rekommenderar när kunden vill ha modern inredning och stor planlösningsvalfrihet till ett rimligt pris.",
    layouts: [
      "Sport 500 med fransk bädd bak och kompakt våtrum",
      "Sport 540 med enkelsängar och stort kök längs sidan",
      "Sport 580 familjeplanlösning med våningssäng och separat toalettdel",
    ],
    perfectFor: [
      "Nya husvagnsägare som vill ha mycket utrustning per krona",
      "Familjer som campar i Sverige och på kontinenten under sommarhalvåret",
      "Dragbilar från 1 800 kg dragvikt beroende på modell",
    ],
    strengths: [
      "Bred planlösningsflora — det finns nästan alltid en variant som passar",
      "Modern inredningskänsla med ljusa ytor och bra belysning",
      "Konkurrenskraftigt begagnatpris jämfört med nordiska premiummärken",
      "Enkel serviceåtkomst till värmare och vattenpump",
    ],
    weaknesses: [
      "Grundutförandet är inte byggt för nordisk vinter — vinterpaket behövs",
      "Tunnare golvkonstruktion ger kallare golv vid minusgrader",
      "Fuktkänsliga skruvinfästningar i takräcken på tidiga årsmodeller",
    ],
    winter:
      "Med vinterpaket, isolerad tanklåda och elanslutning fungerar Sport ner mot -15 grader. Utan vinterpaket fryser färskvattentanken snabbt, och vi har flera fall per säsong där tömningsventilen spruckit efter en enda kall natt.",
    priceNew: "Nya modeller från cirka 340 000 kr beroende på planlösning",
    priceUsed:
      "2014–2016: 115 000–185 000 kr · 2017–2019: 180 000–265 000 kr · 2020–2023: 260 000–420 000 kr",
    vanligaFel: [
      "Fukt vid takräckets infästningar där tätmassan torkat och spruckit",
      "Kondens i taklampornas urtag som ger missfärgning i panelen",
      "Vattenpumpens tryckvakt som pulserar när backventilen läcker",
      "Frusen och sprucken tömningsventil efter vinterbruk utan vinterpaket",
    ],
    checklist: [
      "Fuktmätning i 12 punkter inklusive taket vid räckesinfästningar",
      "Kontroll av att vinterpaket verkligen finns monterat — det står inte alltid i annonsen",
      "Provkörning av värmaren i 45 minuter med kontroll av utblåstemperatur",
      "Kontroll av däckens ålder och att reservhjulet finns och håller tryck",
      "Genomgång av 12 V-centralen med mätning av spänningsfall vid belastning",
    ],
    djup: [
      "Knaus Sport är byggd för centraleuropeisk camping, och det är nyckeln till att förstå vagnen. Konstruktionen är lätt och kostnadseffektiv, vilket ger utmärkta köregenskaper och låg bränsleförbrukning för dragbilen — men den är inte dimensionerad för att stå uppställd i -25 grader i tre månader. Köper du en Sport för svenskt bruk är vinterpaket och isolerad tanklåda inte ett tillval utan en nödvändighet.",
      "Ur ett prisperspektiv är serien svår att slå. En 2019-vagn kostar typiskt 60 000–90 000 kr mindre än en nordisk premiumvagn av samma årgång och storlek. Under åtta års ägande är det pengar som räcker till mycket underhåll — men värdeminskningen är procentuellt högre, kring 9–11 procent per år, så jämför alltid totalkostnad och inte bara inköpspris.",
      "Takräcket är seriens mest kända svaga punkt hos oss. Infästningarna sitter genom takskiktet, och när originaltätningen åldrats mellan år 6 och 9 börjar fukten vandra. Åtgärden är enkel om den görs i tid: rengöring, ny tätmassa och kontrollmätning kostar några tusen kronor. Görs det inte kan panelbyte kosta tiotusentals.",
      "Vi ser också att fler Sport-vagnar än snittet är eftermonterade med mover. Det är i sig bra, men monteringen belastar chassibalkarna, och vid vår kontroll mäter vi att fästklamrarna sitter enligt tillverkarens moment. En glappande mover slår sönder bromsstaget över tid, vilket är en dyr och helt undvikbar skada.",
    ],
    faq: [
      {
        q: "Behöver Knaus Sport vinterpaket i Sverige?",
        a: "Ja, om vagnen ska användas när det är minusgrader. Utan paketet fryser vattensystemet snabbt.",
      },
      {
        q: "Vilken planlösning är mest praktisk?",
        a: "Enkelsängar bak är enklast att leva med för par, medan familjer nästan alltid landar i våningssängsmodellen.",
      },
      {
        q: "Hur ser andrahandsvärdet ut?",
        a: "Något svagare än nordiska märken, men efterfrågan är stabil på välskötta exemplar med rent fuktprotokoll.",
      },
    ],
  },
  {
    brandSlug: "adria",
    brandName: "Adria",
    category: "husvagn",
    slug: "altea",
    name: "Adria Altea",
    h1: "Adria Altea — lätt vagn för familjer med liten dragbil",
    metaTitle: "Adria Altea begagnad: pris, vikt och vad vi kontrollerar",
    metaDesc:
      "Adria Altea begagnad husvagn: prisspann per årsmodell, lastvikter, planlösningar för familjer och de fel vi återkommande åtgärdar i Kalix.",
    keyword: "adria altea",
    intro:
      "Altea är Adrias lätta familjeserie och lösningen för dig vars dragbil bara klarar runt 1 500 kg. Vi säljer den ofta till förstagångsköpare med små barn.",
    layouts: [
      "Altea 472 med våningssäng bak och lågt kultryck",
      "Altea 502 med fransk bädd och stor sittgrupp",
      "Altea 552 med enkelsängar för den som vill slippa bädda om",
    ],
    perfectFor: [
      "Familjer med dragbil på 1 500–1 800 kg dragvikt",
      "Kortare campingresor i Sverige under sommaren",
      "Den som prioriterar låg vikt och enkel hantering framför lyx",
    ],
    strengths: [
      "Tjänstevikt från cirka 1 000 kg — går att dra med många vanliga familjebilar",
      "Prisvärd instegsmodell med hela planlösningar för fem sovplatser",
      "Lätt att rangera för hand tack vare låg vikt",
      "Enkel teknik som är billig att serva",
    ],
    weaknesses: [
      "Begränsad lastvikt — ofta bara 120–180 kg kvar med utrustning",
      "Tunn isolering gör vagnen olämplig för kyla",
      "Enkla ytmaterial som slits snabbare vid intensivt familjebruk",
    ],
    winter:
      "Altea är en sommarvagn. Vi rekommenderar att den töms och vinteravställs senast i oktober i Norrbotten. Vid vinterförvaring utomhus bör taket skottas och stödbenen avlastas för att undvika deformation.",
    priceNew: "Nya lätta Adria-modeller från cirka 290 000 kr",
    priceUsed:
      "2012–2015: 78 000–125 000 kr · 2016–2018: 120 000–178 000 kr · 2019–2022: 175 000–265 000 kr",
    vanligaFel: [
      "Överlastade vagnar med däckslitage på innerkanten som följd",
      "Fukt vid bakvägg när cykelhållare monterats utan förstärkning",
      "Trasiga lådskenor i köket efter tung packning på grusväg",
      "Lös bordsinfästning som glappar när bordet används som säng",
    ],
    checklist: [
      "Vägning av vagnen i körklart skick — vi väger på plats innan leverans",
      "Fuktmätning i 12 punkter med extra kontroll runt bakre infästningar",
      "Kontroll av däcktryck mot faktisk axellast, inte mot generella tabellvärden",
      "Genomgång av kultryck med normal packning — mål 50–75 kg beroende på dragbil",
      "Funktionsprov av samtliga sovplatser inklusive våningssängens säkerhetsnät",
    ],
    djup: [
      "Det viktigaste med Altea är viktekvationen. Med en tjänstevikt runt 1 050 kg och totalvikt på 1 350 kg finns ungefär 300 kg lastvikt på pappret, men förtält, markis och cykelhållare tar snabbt 120 kg. Sedan ska packning, vatten och gasol in. Vi väger därför alltid vagnen i körklart skick innan leverans så att kunden vet exakt hur mycket som får lastas.",
      "För dragbilen betyder den låga vikten mycket. Många moderna SUV:ar med 1 600 kg dragvikt kan inte dra en normalstor vagn lagligt, men klarar en Altea med marginal. Det gör serien till en av få vägar in i husvagnslivet utan att först byta bil, vilket i praktiken sparar 100 000 kr eller mer.",
      "Konstruktionsmässigt är väggarna tunnare och isoleringen enklare än på tyngre serier. Det märks direkt vid höstcamping: vagnen värms fort men tappar också värmen fort. Vi brukar rekommendera termogardiner till framrutan och ett extra golvunderlägg, vilket för några tusen kronor gör septembercamping betydligt behagligare.",
      "Vid inbyte är fuktprotokollet och vägningsprotokollet det som avgör värdet. En Altea som körts överlastad syns nästan alltid: däckslitaget på innerkanten, glapp i axelinfästningen och slitna lådskenor är fingeravtryck vi känner igen direkt. Har vagnen körts inom vikterna håller den däremot förvånansvärt bra.",
    ],
    faq: [
      {
        q: "Hur mycket får jag lasta i en Adria Altea?",
        a: "Ofta 120–250 kg beroende på modell och utrustning. Vi väger alltid vagnen så att du får exakta siffror.",
      },
      {
        q: "Kan man vintercampa med Altea?",
        a: "Det är inte vad vagnen är byggd för. Höstcamping fungerar med elvärme, men vintern kräver en tyngre och bättre isolerad vagn.",
      },
      {
        q: "Räcker vår familjebil som dragbil?",
        a: "Kolla registreringsbeviset: bilens maximala släpvagnsvikt måste överstiga vagnens totalvikt. Vi hjälper till att räkna innan köp.",
      },
    ],
  },
  {
    brandSlug: "rapido",
    brandName: "Rapido",
    category: "husbil",
    slug: "serie-8",
    name: "Rapido 8-serien",
    h1: "Rapido 8-serien — halvintegrerad med fransk finish",
    metaTitle: "Rapido 8-serien begagnad husbil: pris, vikter och kontroller",
    metaDesc:
      "Rapido 8-serien begagnad: prisspann per årsmodell, planlösningar, vinterduglighet i Norrbotten, återkommande fel och vår kontroll före köp.",
    keyword: "rapido 8-serien",
    intro:
      "8-serien är Rapidos halvintegrerade mittklass och den husbil vi känner bäst av alla — vi är Rapido-verkstad och har delar och erfarenhet på hyllan i Kalix.",
    layouts: [
      "Enkelsängar bak med stort garage under — mest efterfrågat hos par",
      "Ö-säng bak med genomgående våtrum över hela bredden",
      "Nedsänkbar himmelssäng fram i kombination med sittgrupp för fyra",
    ],
    perfectFor: [
      "Par som kör långa resor och vill ha bra körkomfort",
      "Den som vill ha fransk inredningskvalitet och tyst kupé",
      "Ägare som vill ha märkesverkstad i Norrbotten i stället för 100 mil bort",
    ],
    strengths: [
      "Mycket god ljudisolering i kupén jämfört med jämnstora konkurrenter",
      "Genomtänkta möbelinfästningar som håller efter många mil grusväg",
      "Bra viktbalans mellan axlarna vilket ger stabil körning i sidvind",
      "Vi lagerför originaldelar och känner konstruktionen i detalj",
    ],
    weaknesses: [
      "Högre inköpspris begagnat än volymmärken i samma storlek",
      "Vissa inredningsdetaljer är märkesspecifika och kräver beställning",
      "Garageutrymmets lastgräns nås snabbt vid två elcyklar",
    ],
    winter:
      "8-serien klarar svensk vinter väl med dieselvärmare och isolerade avloppsrör. Vi ser dock att förarhyttens vindruta är den svagaste punkten termiskt, och rekommenderar alltid ett yttre vinterskydd som sänker värmeförlusten märkbart vid -20 grader.",
    priceNew: "Nya jämförbara modeller från cirka 1 150 000 kr",
    priceUsed:
      "2014–2016: 480 000–650 000 kr · 2017–2019: 640 000–880 000 kr · 2020–2023: 870 000–1 250 000 kr",
    vanligaFel: [
      "Läckage vid takluckans ram efter 7–9 år när originaltätningen hårdnat",
      "Kärvande himmelssäng när lyftmotorns styrning tappat kalibrering",
      "Kondens i garaget vid vinterbruk om ventilationsgallret täckts av last",
      "Slitna bussningar i förarhyttens fjädring efter mycket grusväg",
    ],
    checklist: [
      "Fuktmätning i 12 punkter inklusive garagegolv och hjulhusvinklar",
      "Diagnosläsning av chassit för lagrade felkoder i motor- och ABS-system",
      "Funktionsprov av dieselvärmare i 45 minuter med kontroll av avgastemperatur",
      "Kontroll av himmelssängens lyftmekanism och nödsänkning",
      "Vägning per axel för att säkerställa att bakaxeln inte överlastas",
    ],
    djup: [
      "Att vi är Rapido-verkstad påverkar hur vi bedömer 8-serien. Vi har sett samma bilar återkomma på service i tio år och vet vilka detaljer som håller. Möbelstommarna är ett tydligt exempel: där många konkurrenter får glapp i skruvförband efter 8 000 mil grusväg står Rapidos skåp fast, vilket också syns i lägre knarrnivå i kupén.",
      "Vikt är ändå den vanligaste besvikelsen. En 8-serie på 3 500 kg totalvikt har ofta 350–500 kg kvar i lastvikt efter fabriksutrustning. Två elcyklar väger 60 kg, full färskvattentank 100 kg och två personer plus packning ytterligare 200 kg — då är marginalen borta. Vi väger bilen per axel innan leverans och visar exakt vad som ryms.",
      "Ekonomiskt håller Rapido värdet bättre än volymmärken. En bil för 900 000 kr ligger efter fyra år ofta kring 700 000–760 000 kr, vilket motsvarar 5–7 procent per år. Räknat per resdygn för en ägare som kör 50 nätter per år är det cirka 900 kr per natt i ren värdeminskning, innan drivmedel och service.",
      "Vid vinterbruk är det värmarens dimensionering som avgör upplevelsen. Dieselvärmare med vattenburen distribution ger jämnast temperatur, medan luftvärmare värmer snabbare men torrare. Vi mäter alltid golvtemperatur vid sängen under provkörning eftersom det är där kunder märker skillnaden efter en natt i -20 grader.",
    ],
    faq: [
      {
        q: "Varför köpa Rapido i stället för ett volymmärke?",
        a: "För byggkvalitet, tystnad i kupén och att vi har märkeskunskapen och delarna på plats i Kalix.",
      },
      {
        q: "Räcker B-körkort?",
        a: "På 3 500 kg-versionerna ja. Bilar uppgraderade till 4 250 kg eller mer kräver utökad behörighet.",
      },
      {
        q: "Hur mycket får jag lasta i garaget?",
        a: "Vanligen 150 kg, men kontrollera skylten i garaget — vissa planlösningar har lägre gräns.",
      },
    ],
  },
  {
    brandSlug: "rapido",
    brandName: "Rapido",
    category: "husbil",
    slug: "c-serien",
    name: "Rapido C-serien",
    h1: "Rapido C-serien — kompakt husbil utan kompromiss på kvalitet",
    metaTitle: "Rapido C-serien begagnad: pris, mått och våra kontrollpunkter",
    metaDesc:
      "Rapido C-serien begagnad husbil: prisspann, längder, planlösningar, vad som fungerar i vinterklimat och vilka fel vi återkommande åtgärdar.",
    keyword: "rapido c-serien",
    intro:
      "C-serien är Rapidos kompaktare familj för dig som vill ha en husbil under sju meter men behålla känslan av en större bil. Vi säljer den ofta till par som kör mycket i Norge och på smala fjällvägar.",
    layouts: [
      "Kompakt planlösning med fransk bädd bak och våtrum i hörn",
      "Enkelsängar bak i de längre varianterna med lägre garage",
      "Sittgrupp med två separata stolar i stället för soffa för bättre körkomfort",
    ],
    perfectFor: [
      "Par som kör smala vägar, färjor och fjällpass",
      "Den som vill parkera bilen i normala parkeringsrutor",
      "Ägare som prioriterar bränsleekonomi framför maximal boyta",
    ],
    strengths: [
      "Kortare byggnadslängd ger märkbart lättare hantering i trånga lägen",
      "Lägre luftmotstånd — vi ser ofta 0,8–1,2 liter lägre förbrukning per 100 km än på stora integrerade",
      "Samma möbelkvalitet som i de större serierna",
      "Enklare att vinterförvara inomhus tack vare måtten",
    ],
    weaknesses: [
      "Mindre garage och färre förvaringsmeter än i 8-serien",
      "Trängre våtrum som kan kännas snålt för längre personer",
      "Begränsad plats för stora solcellsinstallationer på taket",
    ],
    winter:
      "Kompakta bilar värms fort, vilket är en fördel i kyla: vi mäter ofta uppvärmning från 0 till 20 grader på under 40 minuter med dieselvärmare. Nackdelen är att kondens uppstår snabbare vid matlagning, så forcerad ventilation eller takluckan på glänt är viktigare här än i en stor bil.",
    priceNew: "Nya kompaktmodeller från cirka 990 000 kr",
    priceUsed:
      "2013–2016: 410 000–580 000 kr · 2017–2019: 570 000–760 000 kr · 2020–2023: 750 000–1 090 000 kr",
    vanligaFel: [
      "Fukt i hörnet bakom våtrummet där vattenledningen passerar ytterväggen",
      "Slitage i förarstolens vridmekanism efter många vridningar",
      "Sprickbildning i plastdetaljer runt fotsteget vid intensiv vinteranvändning",
      "Igensatt avlopp i duschen på grund av trång rördragning",
    ],
    checklist: [
      "Fuktmätning i 12 punkter med särskilt fokus bakom våtrummet",
      "Kontroll av fotstegets funktion och elmotorns strömförbrukning",
      "Provkörning på minst 3 mil med kontroll av vibrationer i drivlinan",
      "Kontroll av tak och solcellsfästen med fuktindikator",
      "Test av avloppsflöde med 10 liter vatten för att hitta partiella stopp",
    ],
    djup: [
      "Kompakt är inte samma sak som liten i praktisk mening. Skillnaden mellan 6,5 och 7,4 meter avgör om du kan ta färjan utan tilläggsavgift, parkera vid en normal butik och vända på en skogsbilväg i Norrbotten. Vi har kunder som bytt ned sig från en integrerad bil och som säger att de kör 30–40 procent fler resdygn per år efter bytet, helt enkelt för att tröskeln att åka iväg blev lägre.",
      "Bränsleekonomin är mätbar. På våra provkörningar mellan Kalix och Luleå ligger C-serien typiskt på 9,5–11 liter per 100 km medan en stor integrerad hamnar på 11–13 liter. Över 2 000 mil per år motsvarar det ungefär 6 000–9 000 kr i skillnad — inte avgörande, men värt att räkna med i totalkalkylen.",
      "Den vanligaste kompromissen är förvaring. Där en 8-serie tar två elcyklar tar C-serien ofta bara en, plus stolar. Lösningen vi rekommenderar är en dragkroksmonterad cykelhållare, men då måste bakaxelbelastningen kontrolleras — kombinationen av tungt garage och cykelhållare bak är det snabbaste sättet att överlasta bakaxeln utan att totalvikten överskrids.",
      "På sikt är C-serien lätt att sälja vidare eftersom efterfrågan på kompakta husbilar ökat kraftigt sedan 2020. Vi ser kortare liggtider på kompakta bilar än på stora integrerade, ofta hälften så lång tid från annons till avslut i samma prisklass.",
    ],
    faq: [
      {
        q: "Hur lång är en Rapido C-serie?",
        a: "Beroende på modell cirka 6,0 till 7,0 meter, vilket gör den parkeringsvänlig jämfört med större husbilar.",
      },
      {
        q: "Fungerar den för två personer på längre resor?",
        a: "Ja, för par är den fullt tillräcklig. För fyra personer med packning blir den trång.",
      },
      {
        q: "Är förbrukningen verkligen lägre?",
        a: "Ja, vi mäter typiskt 1 liter mindre per 100 km än jämförbara stora integrerade bilar.",
      },
    ],
  },
  {
    brandSlug: "hymer",
    brandName: "Hymer",
    category: "husbil",
    slug: "b-klass",
    name: "Hymer B-klass",
    h1: "Hymer B-klass — integrerad husbil med tysk stomme",
    metaTitle: "Hymer B-klass begagnad: pris, vikter och kontroll före köp",
    metaDesc:
      "Hymer B-klass begagnad husbil: prisspann per årsmodell, planlösningar, dubbelgolv och vinterprestanda samt fel vi återkommande åtgärdar i verkstaden.",
    keyword: "hymer b-klass",
    intro:
      "B-klass är Hymers integrerade serie och en av få husbilar vi utan förbehåll rekommenderar för vinterbruk i Norrbotten tack vare uppvärmt dubbelgolv.",
    layouts: [
      "Enkelsängar bak med garage under och nedsänkbar säng fram",
      "Ö-säng bak för den som vill kunna gå runt sängen",
      "Sittgrupp fram med panoramaruta och två vridbara förarstolar",
    ],
    perfectFor: [
      "Vinterresor och skidsemestrar där bilen används i sträng kyla",
      "Par som vill ha maximal boendekänsla och stora fönsterytor",
      "Ägare som accepterar högre totalvikt för bättre komfort",
    ],
    strengths: [
      "Uppvärmt dubbelgolv som håller vattentankar och ledningar frostfria",
      "PUAL-stomme utan trä i väggen på nyare årsmodeller — betydligt lägre fuktrisk",
      "Panoramaruta ger ljus kupé även i mörka vintermånader",
      "Stabil chassiuppbyggnad, ofta på förstärkt AL-KO-ram",
    ],
    weaknesses: [
      "Hög totalvikt — många exemplar kräver mer än B-behörighet",
      "Stora glasytor ger värmeförlust utan monterade isolermattor",
      "Dyra reparationer på framruta och integrerad frontkonstruktion",
    ],
    winter:
      "Med uppvärmt dubbelgolv och vattenburen värme har vi mätt bibehållen kupétemperatur på 21 grader vid -28 grader utomhus, med tankarna frostfria. Framrutan är fortfarande den största värmeläckan och bör täckas med invändiga eller yttre mattor vid längre uppställning.",
    priceNew: "Nya integrerade Hymer-modeller från cirka 1 450 000 kr",
    priceUsed:
      "2013–2015: 520 000–720 000 kr · 2016–2018: 700 000–980 000 kr · 2019–2023: 950 000–1 650 000 kr",
    vanligaFel: [
      "Kondens mellan glasrutorna i dubbelglasfönster som tappat tätning",
      "Slitna gasfjädrar till sänglyft som sjunker under natten",
      "Läckage vid takluckans främre hörn efter stenskott och tvätt med högtryck",
      "Fel i el-centralen där en korroderad jordpunkt ger slumpvisa spänningsfall",
    ],
    checklist: [
      "Fuktmätning i 12 punkter samt kontroll av dubbelgolvets inspektionsluckor",
      "Kontroll av samtliga fönster för imma mellan rutorna",
      "Axelvis vägning och kontroll av registrerad maxlast mot körkortsbehörighet",
      "Genomgång av värmesystemets expansionskärl och glykolnivå",
      "Kontroll av jordpunkter och spänningsfall vid 20 A belastning",
    ],
    djup: [
      "Dubbelgolvet är hela poängen med B-klass i vårt klimat. Tankar, ledningar och avlopp ligger i ett uppvärmt utrymme i stället för exponerat under bilen. Praktiskt betyder det att du kan använda vatten och toalett en hel vintervecka utan frysrisk, vilket är skillnaden mellan camping och verkligt boende när termometern visar -25.",
      "Viktfrågan måste redas ut före köp. Många B-klassbilar är registrerade på 3 500 kg men har hög tjänstevikt, och med full utrustning återstår ibland bara 250–350 kg lastvikt. Uppgradering till 4 250 kg finns på vissa chassin men kräver utökad behörighet. Vi går igenom registreringsbeviset och väger bilen axelvis så att du vet exakt var gränsen går.",
      "Fönstren avslöjar bilens historia. Imma mellan rutorna i dubbelglas betyder att tätningen släppt, och även om det inte påverkar konstruktionen så kostar byte 8 000–20 000 kr per fönster. Vi noterar varje sådant fönster i vår besiktning och prissätter bilen därefter i stället för att låta problemet dyka upp hos kunden efter köpet.",
      "PUAL-stommen på nyare årsmodeller är ett verkligt framsteg för hållbarhet, eftersom väggen inte har trä som kan ta upp fukt. Vi mäter ändå alltid, för fukten kan finnas i golvet eller i infästningar. Skillnaden är att en fuktskada på PUAL oftare är lokal och åtgärdbar, medan en trästomme kan behöva omfattande demontering.",
    ],
    faq: [
      {
        q: "Är Hymer B-klass bra för vintercamping?",
        a: "Ja, det är en av de bästa serierna för vinterbruk tack vare uppvärmt dubbelgolv och vattenburen värme.",
      },
      {
        q: "Räcker B-körkort till en B-klass?",
        a: "På 3 500 kg-registrerade bilar ja, men lastutrymmet blir litet. Många väljer uppgraderat chassi och utökad behörighet.",
      },
      {
        q: "Vad kostar underhållet?",
        a: "Räkna med 8 000–15 000 kr per år i service och tätningsunderhåll för en bil som används mycket.",
      },
    ],
  },
  {
    brandSlug: "burstner",
    brandName: "Bürstner",
    category: "husbil",
    slug: "lyseo",
    name: "Bürstner Lyseo",
    h1: "Bürstner Lyseo — halvintegrerad allroundare",
    metaTitle: "Bürstner Lyseo begagnad husbil: pris, planlösningar och fel",
    metaDesc:
      "Bürstner Lyseo begagnad: prisspann per årsmodell, planlösningar, vad vi ser på fukt och el, samt hur den fungerar i svenskt vinterklimat.",
    keyword: "bürstner lyseo",
    intro:
      "Lyseo är Bürstners breda halvintegrerade serie och en bil vi ofta rekommenderar när kunden vill ha modern inredning och stor valfrihet i planlösning till ett rimligt pris.",
    layouts: [
      "Lyseo TD med enkelsängar bak och stort garage",
      "Lyseo Harmony Line med uppgraderad inredning och sänglyft fram",
      "Kortare varianter med fransk bädd för par som vill ha smidiga mått",
    ],
    perfectFor: [
      "Par och små familjer som reser mest under vår, sommar och höst",
      "Den som vill ha modern inredningsdesign till mellanklasspris",
      "Köpare som vill ha bra utrustning per krona snarare än extrem vinterprestanda",
    ],
    strengths: [
      "Ljus inredning med genomtänkt belysning och bra köksbänkyta",
      "Bra pris i förhållande till utrustning på begagnatmarknaden",
      "Enkel serviceåtkomst till värmare, batteri och vattenpump",
      "Stort utbud av planlösningar gör det lätt att hitta rätt variant",
    ],
    weaknesses: [
      "Grundutförandet är byggt för mildare klimat än det norrbottniska",
      "Vissa årsmodeller har svaga garagegolvsinfästningar vid tung last",
      "Ytmaterialen tål inte lika hård familjeanvändning som tyngre märken",
    ],
    winter:
      "Med vinterpaket och isolerade avlopp fungerar Lyseo ner mot -15 grader utan problem. Under det behöver du täcka framrutan och hålla värmaren igång dygnet runt, eftersom golvet inte har uppvärmt dubbelgolv i grundutförandet.",
    priceNew: "Nya modeller från cirka 1 050 000 kr beroende på planlösning",
    priceUsed:
      "2014–2016: 420 000–590 000 kr · 2017–2019: 580 000–790 000 kr · 2020–2023: 780 000–1 150 000 kr",
    vanligaFel: [
      "Fukt vid garageluckans nedre tätning där rengöring med högtryck slitit gummit",
      "Glapp i sänglyftens styrlinor som ger sned nedsänkning",
      "Kylskåp som varnar vid höga utetemperaturer på grund av dålig ventilation bakom gallret",
      "Sprickor i markisens infästning när den lämnats utfälld i blåst",
    ],
    checklist: [
      "Fuktmätning i 12 punkter inklusive garagets golv och sidoväggar",
      "Kontroll av garageluckans tätning och gångjärn under belastning",
      "Provkörning av sänglyft 5 gånger med kontroll av linspänning",
      "Kontroll av kylskåpsventilationens gallerplacering och renhet",
      "Elprov med belastning på 15 A för att hitta glappkontakter i centralen",
    ],
    djup: [
      "Lyseo är en typisk kompromissbil i ordets bästa mening: den gör det mesta bra utan att vara extrem åt något håll. Kupén är ljus, köket användbart och planlösningsutbudet stort. Det gör att vi kan matcha rätt variant mot hur kunden faktiskt reser — och just planlösningen är det som avgör om en husbil används 20 eller 60 nätter per år.",
      "Prisbilden är seriens starkaste kort. Jämfört med franska och tyska premiummärken ligger en Lyseo ofta 100 000–200 000 kr lägre i samma årgång och storlek. För en köpare som reser sommartid och förvarar bilen på vintern är det en helt rationell affär, eftersom merparten av premiumkonstruktionen betalar för vinteregenskaper man inte utnyttjar.",
      "Garaget är den detalj vi kontrollerar hårdast. Bürstner har lätta garagegolv, och kunder som lastar två tunga elcyklar plus verktyg kommer nära gränsen. Vi kontrollerar infästningarna underifrån och rekommenderar en förstärkningsplåt när bilen ska bära mer än 100 kg regelbundet — en billig åtgärd som förhindrar deformation.",
      "Vid vinterförvaring i Norrbotten är rutinen viktigare än bilen. Töm vattensystemet helt, lämna avtappningskranarna öppna, ta ur färskvattenfiltret och håll batteriet på underhållsladdning. Vi ser varje vår ett antal bilar med spruckna kopplingar där enda felet var ett kvarglömt kvartsfullt vattenrör.",
    ],
    faq: [
      {
        q: "Är Bürstner Lyseo bra begagnat?",
        a: "Ja, prisvärt och praktiskt. Kontrollera fukt i garaget och att vinterpaket finns om du tänker köra i kyla.",
      },
      {
        q: "Hur mycket lastvikt finns kvar?",
        a: "Ofta 300–450 kg på 3 500 kg-versionerna beroende på utrustning. Vi väger bilen innan leverans.",
      },
      {
        q: "Fungerar den på vinterresor?",
        a: "Ja, för resor med tillsyn. För uppställning i sträng kyla är dubbelgolvsbilar ett tryggare val.",
      },
    ],
  },
  {
    brandSlug: "burstner",
    brandName: "Bürstner",
    category: "husbil",
    slug: "ixeo",
    name: "Bürstner Ixeo",
    h1: "Bürstner Ixeo — sänglyft och smart yta på liten längd",
    metaTitle: "Bürstner Ixeo begagnad: pris, mått och våra kontrollpunkter",
    metaDesc:
      "Bürstner Ixeo begagnad husbil: prisspann, planlösningar med sänglyft, hur den fungerar i kyla och vilka fel vi återkommande åtgärdar i Kalix.",
    keyword: "bürstner ixeo",
    intro:
      "Ixeo bygger på idén att utnyttja höjden i stället för längden: nedsänkbar säng fram frigör golvyta, vilket ger en förvånansvärt rymlig bil på måttlig längd.",
    layouts: [
      "Ixeo Time med sänglyft fram och kompakt kök längs sidan",
      "Ixeo med enkelsängar bak plus sänglyft — fyra fullvärdiga sovplatser",
      "Kortare variant med hörnvåtrum och stor sittgrupp",
    ],
    perfectFor: [
      "Familjer som behöver fyra sovplatser utan att köpa en sju meter lång bil",
      "Den som vill ha stor golvyta dagtid och sovplats som fälls undan",
      "Resor där bilen används som bas i flera dagar på samma plats",
    ],
    strengths: [
      "Sänglyft frigör hela golvytan dagtid — kupén känns mycket större än måtten antyder",
      "Fyra sovplatser utan att behöva bädda om soffan",
      "God takhöjd även med sängen i övre läge",
      "Bra pris begagnat i förhållande till antal sovplatser",
    ],
    weaknesses: [
      "Sänglyftens mekanik är en extra felkälla som måste kontrolleras",
      "Något högre bygghöjd påverkar sidvindskänslighet och förbrukning",
      "Trängre förvaring i takskåp där lyftmekaniken tar plats",
    ],
    winter:
      "Ixeo klarar höst och tidig vinter bra med vinterpaket. Vid riktigt låga temperaturer samlas den varmaste luften uppe vid sänglyften, vilket gör att golvet upplevs kallt. En cirkulationsfläkt eller golvvärmematta löser det för några tusen kronor.",
    priceNew: "Nya jämförbara modeller från cirka 1 020 000 kr",
    priceUsed:
      "2013–2016: 390 000–560 000 kr · 2017–2019: 550 000–740 000 kr · 2020–2023: 730 000–1 060 000 kr",
    vanligaFel: [
      "Sänglyft som stannar i mellanläge på grund av slitna ändlägesgivare",
      "Knarr i lyftpelarna när glidytorna torkat och behöver smörjas",
      "Fukt i takskåpens bakkant där kondens samlas i kallt väder",
      "Trasig fjärrkontroll till lyften — reservdel som ofta saknas vid köp",
    ],
    checklist: [
      "Provkörning av sänglyft minst 5 cykler inklusive nödmanövrering",
      "Fuktmätning i 12 punkter med extra kontroll i takskåp och taknära hörn",
      "Kontroll av lyftmotorns strömförbrukning mot tillverkarens värde",
      "Kontroll av att fjärrkontroll och manuell nödvev finns med",
      "Genomgång av takets tätningar där lyftens infästningar går upp",
    ],
    djup: [
      "Sänglyften är seriens själ och dess största kontrollpunkt. En fungerande lyft ger en sexmetersbil golvyta som en sjumetersbil, men en trasig lyft gör bilen nästan obrukbar för fyra personer. Därför kör vi minst fem hela cykler vid inbytesbesiktning, mäter motorströmmen och kontrollerar att nödmanövreringen faktiskt fungerar — inte bara att den finns.",
      "Termiskt beter sig Ixeo annorlunda än en vanlig halvintegrerad. Eftersom volymen är hög samlas varmluften uppe, och vid -15 grader kan skillnaden mellan golvnivå och sängnivå vara 6–8 grader. Vi rekommenderar en liten cirkulationsfläkt som flyttar ner varmluften; det jämnar ut temperaturen och minskar kondensen i takskåpen.",
      "Ekonomiskt är Ixeo intressant för familjer. Fyra fullvärdiga sovplatser i en bil under 6,5 meter kostar begagnat ofta 100 000–150 000 kr mindre än en lika sovplatsstark bil på sju meter, samtidigt som förbrukningen är lägre och parkeringen enklare. Nackdelen är färre förvaringsmeter, så packningsdisciplin krävs.",
      "Vi ser också att lyftsystemet påverkar andrahandsvärdet oproportionerligt. Två identiska bilar där den ena har servicedokumentation på lyften kan skilja 30 000–40 000 kr i marknadsvärde, helt enkelt för att köparna vet att en lyftreparation kan kosta 15 000–25 000 kr. Spara alltid kvitton på lyftservice.",
    ],
    faq: [
      {
        q: "Är sänglyften pålitlig?",
        a: "Ja, om den servas och smörjs. Vi provkör alltid minst fem cykler och kontrollerar nödmanövreringen innan leverans.",
      },
      {
        q: "Hur många sover bekvämt i en Ixeo?",
        a: "Fyra personer i fasta bäddar i de större planlösningarna, två i de kompakta.",
      },
      {
        q: "Passar den för vinterresor?",
        a: "För resor med tillsyn ja, med vinterpaket. Golvet blir kallare än i dubbelgolvsbilar.",
      },
    ],
  },
  {
    brandSlug: "knaus",
    brandName: "Knaus",
    category: "husbil",
    slug: "van-ti",
    name: "Knaus Van TI",
    h1: "Knaus Van TI — halvintegrerad med hög körkänsla",
    metaTitle: "Knaus Van TI begagnad husbil: pris, vikter och kontroller",
    metaDesc:
      "Knaus Van TI begagnad: prisspann per årsmodell, planlösningar, hur den fungerar i svenskt vinterklimat och de fel vi återkommande åtgärdar.",
    keyword: "knaus van ti",
    intro:
      "Van TI är Knaus mest sålda husbil i Norden och ett bra val när du vill ha modern teknik och bra körkänsla utan att kliva upp i premiumprisklassen.",
    layouts: [
      "Van TI med enkelsängar bak och garage under",
      "Van TI Plus på Mercedes-chassi med bakhjulsdrift",
      "Kompakt variant med fransk bädd för par",
    ],
    perfectFor: [
      "Den som vill ha modern chassiteknik och körkomfort",
      "Par som reser långt och ofta på motorväg",
      "Köpare som vill ha bra utrustningsnivå per investerad krona",
    ],
    strengths: [
      "Bra köregenskaper, särskilt på Mercedes-baserade varianter",
      "Modern inredningsdesign med god ljusinsläpp",
      "Stark utrustningsnivå som standard i senare årsmodeller",
      "Stort återförsäljarnät i Europa ger enkel service på resa",
    ],
    weaknesses: [
      "Vinteregenskaperna kräver rätt paket — kontrollera noga vid köp",
      "Garagets lastgräns är lägre än på flera konkurrenter",
      "Vissa elkomponenter är märkesspecifika med längre leveranstid",
    ],
    winter:
      "Med vinterpaket klarar Van TI svenska vinterresor bra, men avloppsrören är den svaga punkten. Vi isolerar och värmekabelmonterar avloppet på de bilar vi säljer till kunder som verkligen ska köra i minusgrader, vilket i praktiken eliminerar problemet.",
    priceNew: "Nya modeller från cirka 1 080 000 kr",
    priceUsed:
      "2015–2017: 460 000–630 000 kr · 2018–2020: 620 000–860 000 kr · 2021–2023: 840 000–1 190 000 kr",
    vanligaFel: [
      "Frysta avlopp vid vinterkörning utan isolering eller värmekabel",
      "Slitna gångjärn i garageluckan när den öppnats mot vind",
      "Batterifrånskiljare som ger spänningsfall vid korrosion i kontaktstycket",
      "Vattenläckage vid duschslangens anslutning på tidiga årsmodeller",
    ],
    checklist: [
      "Fuktmätning i 12 punkter och kontroll av garagegolvets kanter",
      "Kontroll av att vinterpaket och isolerade tankar verkligen finns",
      "Diagnosläsning av chassit och kontroll av servicehistorik i systemet",
      "Kapacitetstest av förbrukningsbatteri under 10 A last i 60 minuter",
      "Provkörning minst 3 mil med kontroll av bromsar och styrning",
    ],
    djup: [
      "Chassivalet avgör mycket i Van TI. Fiat-baserade bilar är billigare och enklare att serva i Sverige, medan Mercedes-varianterna ger bättre köregenskaper, bakhjulsdrift och ofta högre andrahandsvärde. Skillnaden i begagnatpris ligger typiskt på 80 000–150 000 kr, och för den som kör mycket vintervägar i Norrbotten är bakhjulsdriften värd en stor del av den premien.",
      "Utrustningsnivån varierar mer mellan exemplar än på många andra märken, eftersom Knaus säljer mycket via paket. Två bilar från samma år kan skilja 150 000 kr i nypris. Vi går igenom originalorderns utrustningslista när den finns, så att kunden vet vad som ingår och vad som är eftermonterat.",
      "Avloppsfrågan är den enskilt vanligaste vinterfrågan vi får om serien. Standardavloppet ligger exponerat, och vid -15 grader fryser det inom några timmar när bilen står stilla. Åtgärden — isolering plus självreglerande värmekabel — kostar några tusenlappar och gör bilen praktiskt användbar hela vintern.",
      "Sett över tid håller Van TI värdet acceptabelt: cirka 8–9 procent per år för Fiat-baserade och 6–8 procent för Mercedes-baserade. För en bil köpt på 800 000 kr innebär det ungefär 55 000–70 000 kr per år i värdefall, vilket bör ingå i budgeten tillsammans med försäkring, service och besiktning.",
    ],
    faq: [
      {
        q: "Fiat eller Mercedes som chassi?",
        a: "Mercedes ger bättre körkänsla och bakhjulsdrift men kostar mer. Fiat är billigare i inköp och service.",
      },
      {
        q: "Klarar Knaus Van TI vintern?",
        a: "Ja, med vinterpaket och isolerat avlopp. Utan det fryser avloppet snabbt i Norrbotten.",
      },
      {
        q: "Hur mycket får jag lasta i garaget?",
        a: "Ofta 100–150 kg. Kontrollera dekalen i garaget, den varierar mellan planlösningar.",
      },
    ],
  },
  {
    brandSlug: "dethleffs",
    brandName: "Dethleffs",
    category: "husbil",
    slug: "globebus",
    name: "Dethleffs Globebus",
    h1: "Dethleffs Globebus — kompakt husbil för smala vägar",
    metaTitle: "Dethleffs Globebus begagnad: pris, mått och kontrollpunkter",
    metaDesc:
      "Dethleffs Globebus begagnad husbil: prisspann, längder under sju meter, planlösningar, vinterbruk och fel vi återkommande åtgärdar i verkstaden.",
    keyword: "dethleffs globebus",
    intro:
      "Globebus är Dethleffs kompaktserie och en praktisk bil för den som vill ha husbil men ogillar att köra långa fordon på norrländska skogsvägar och färjeramper.",
    layouts: [
      "Globebus T med enkelsängar bak i den längre varianten",
      "Kompakt variant med fransk bädd och hörnvåtrum",
      "Planlösning med tvärbädd bak och stort förvaringsutrymme under",
    ],
    perfectFor: [
      "Par som kör mycket på smala vägar och vill parkera enkelt",
      "Den som vill ha låg förbrukning och smidiga mått",
      "Förstagångsköpare av husbil som vill börja lagom stort",
    ],
    strengths: [
      "Kort byggnadslängd som ger enkel parkering och bra vändradie",
      "Låg förbrukning i förhållande till boyta",
      "Bra prisbild på begagnatmarknaden",
      "Enkel och robust inredning som är billig att laga",
    ],
    weaknesses: [
      "Mindre förvaringsutrymme än full storlek",
      "Enklare isolering — vinterbruk kräver kompletteringar",
      "Trängre våtrum som kan kännas litet för längre personer",
    ],
    winter:
      "Globebus fungerar för vinterresor med dieselvärmare men saknar uppvärmt dubbelgolv. Vi rekommenderar värmekabel på avlopp, täckt framruta och att färskvattentanken hålls minst halvfull, eftersom en delvis fylld tank fryser långsammare vid korta stopp.",
    priceNew: "Nya kompakta Dethleffs-modeller från cirka 950 000 kr",
    priceUsed:
      "2013–2016: 360 000–520 000 kr · 2017–2019: 510 000–690 000 kr · 2020–2023: 680 000–990 000 kr",
    vanligaFel: [
      "Fukt i hörn bakom våtrummet där rör passerar ytterväggen",
      "Trasiga plastclips i inredningens luckor efter många mil dålig väg",
      "Kondens i förarhytten vid vinterbruk utan invändiga isolermattor",
      "Slitna bromsklossar bak som missas eftersom bilen körs få mil per år",
    ],
    checklist: [
      "Fuktmätning i 12 punkter och kontroll av rörgenomföringar",
      "Bromskontroll med mätning av belägg, extra viktigt på lågmilare",
      "Kontroll av däckens ålder — husbilsdäck byts efter 6–8 år oavsett mönsterdjup",
      "Provkörning av dieselvärmare med kontroll av bränsleförbrukning",
      "Genomgång av tak och takgenomföringar med fuktindikator",
    ],
    djup: [
      "Kompakta husbilar körs ofta färre mil per år än stora, och det skapar en egen sorts slitage. Bromsar rostar när bilen står, däck åldras snabbare än de slits och batterier laddas ur. Vi lägger därför mer tid på bromsar och däckdatum än på motorslitage när vi tar in en Globebus, eftersom det är där de verkliga kostnaderna gömmer sig.",
      "Längden under sju meter öppnar upp Sverige på ett annat sätt. Färjelägen, mindre campingar och naturnära uppställningsplatser i Norrbotten har ofta begränsad plats, och skillnaden mellan 6,4 och 7,4 meter avgör om du får plats. För resande som vill ta sig till småvägar mer än till stora ställplatser är det ett avgörande argument.",
      "Förvaringen är kompromissen. Utan stort garage måste cyklar hanteras på dragkroksmonterad hållare, och då blir bakaxellasten viktig. Vi väger alltid axelvis med tänkt last, eftersom en kompakt bil med tung cykelhållare kan överskrida bakaxelns gräns långt innan totalvikten nås.",
      "Prismässigt är Globebus ett bra sätt att komma in i husbilslivet: en 2017-bil på cirka 600 000 kr ger de flesta funktioner en förstagångsköpare behöver. Efter tre till fyra år vet man vad man faktiskt använder, och då är steget upp till en större bil betydligt mer träffsäkert.",
    ],
    faq: [
      {
        q: "Hur lång är en Dethleffs Globebus?",
        a: "Beroende på variant cirka 6,0 till 7,0 meter, alltså under gränsen där parkering börjar bli besvärlig.",
      },
      {
        q: "Går det att vintercampa i den?",
        a: "Ja, för resor med tillsyn och med dieselvärmare. Vi rekommenderar värmekabel på avloppet.",
      },
      {
        q: "Vad ska jag kolla först på en lågmilare?",
        a: "Bromsar, däckens ålder och batteriets kondition. Låg mätarställning betyder inte lågt slitage på de delarna.",
      },
    ],
  },
  {
    brandSlug: "adria",
    brandName: "Adria",
    category: "husbil",
    slug: "sonic",
    name: "Adria Sonic",
    h1: "Adria Sonic — integrerad toppmodell med lätt konstruktion",
    metaTitle: "Adria Sonic begagnad husbil: pris, vikter och kontroll",
    metaDesc:
      "Adria Sonic begagnad: prisspann per årsmodell, planlösningar, vikt och lastutrymme, vinterprestanda och de fel vi återkommande åtgärdar i Kalix.",
    keyword: "adria sonic",
    intro:
      "Sonic är Adrias integrerade flaggskepp och en bil som kombinerar stor boendekänsla med förvånansvärt låg vikt tack vare kompositkonstruktionen.",
    layouts: [
      "Sonic Supreme med enkelsängar bak, stort garage och nedsänkbar säng fram",
      "Ö-säng bak för den som vill kunna gå runt bädden",
      "Planlösning med sittgrupp fram och panoramaruta",
    ],
    perfectFor: [
      "Par som vill ha integrerad bil men bry sig om lastvikt",
      "Långresenärer som prioriterar boendekomfort och stora fönster",
      "Ägare som vill ha modern teknik och gott om solcellsyta på taket",
    ],
    strengths: [
      "Lätt kompositstomme ger mer lastvikt än många jämnstora integrerade",
      "Stor takyta som rymmer 400–600 W solceller utan trängsel",
      "Ljus och rymlig kupé med bra sikt från förarplatsen",
      "God isolering i tak och väggar för sin viktklass",
    ],
    weaknesses: [
      "Stora glasytor kräver isolermattor vid kyla",
      "Frontskador på integrerade bilar är dyra att åtgärda",
      "Sänglyftens mekanik behöver regelbunden service",
    ],
    winter:
      "Sonic fungerar bra ner mot -20 grader med dieselvärmare och isolerade tankar. Utan täckta rutor tappar bilen mycket värme fram; vi mäter ofta 3–5 graders skillnad i kupén enbart genom att montera yttre framrutemattor.",
    priceNew: "Nya integrerade Adria-modeller från cirka 1 250 000 kr",
    priceUsed:
      "2014–2016: 490 000–660 000 kr · 2017–2019: 650 000–890 000 kr · 2020–2023: 880 000–1 320 000 kr",
    vanligaFel: [
      "Läckage vid framrutans övre list efter stenskott och åldrad tätmassa",
      "Sänglyft som inte går ner helt när linorna sträckts olika mycket",
      "Solcellsfästen som lossnat efter montering utan korrekt förbehandling",
      "Kondens i garaget vid vinterbruk när ventilationen är blockerad av last",
    ],
    checklist: [
      "Fuktmätning i 12 punkter inklusive front och garagets sidoväggar",
      "Kontroll av framrutans tätning och stenskott med sprickrisk",
      "Provkörning av sänglyft med kontroll av linspänning och nödsänkning",
      "Kontroll av solcellsfästen samt mätning av panelernas verkliga effekt",
      "Axelvis vägning för att fastställa faktisk kvarvarande lastvikt",
    ],
    djup: [
      "Sonic löser det klassiska problemet med integrerade bilar: vikten. Kompositkonstruktionen gör att bilen väger mindre än många jämnstora konkurrenter, och det märks direkt i lastvikten där vi ofta ser 400–550 kg kvar på 3 500 kg-versionerna. För en familj som packar mycket kan det vara skillnaden mellan laglig och olaglig last.",
      "Solcellsytan är ett av seriens mest underskattade argument. Taket är stort och relativt fritt från hinder, vilket gör att 500 W är fullt möjligt utan att trängas med luckor och antenner. Med litiumbatteri och den effekten kan bilen stå fritt i flera dagar under sommarhalvåret, vilket i Norrland betyder att du kan ställa dig där utsikten är i stället för där eluttaget finns.",
      "Framrutan är samtidigt den största svagheten. Den är dyr att byta, känslig för stenskott på grusväg och den enskilt största värmeläckan vintertid. Vi rekommenderar alltid yttre isolermattor och kontrollerar rutans kanttätning noggrant vid inbyte, eftersom ett läckage där kan ge fuktskador i förarhyttens golv som är kostsamma att åtgärda.",
      "Marknadsmässigt är efterfrågan på Sonic god men prispressen större än på tyska premiummärken. Räkna med 8–10 procents årlig värdeminskning de första fem åren. Å andra sidan är inköpspriset lägre från början, så kronorna som försvinner per år hamnar ofta i samma nivå som för dyrare bilar med lägre procentuell minskning.",
    ],
    faq: [
      {
        q: "Hur mycket lastvikt har en Adria Sonic?",
        a: "Ofta 400–550 kg på 3 500 kg-versionen, vilket är bra för en integrerad bil. Vi väger alltid axelvis före leverans.",
      },
      {
        q: "Är den bra på vintern?",
        a: "Ja, ner mot -20 grader med dieselvärmare, isolerade tankar och täckta rutor.",
      },
      {
        q: "Hur mycket solceller får plats?",
        a: "400–600 W monteras utan problem på taket, vilket räcker för flera dagar frikoppling sommartid.",
      },
    ],
  },
];
